import * as React from 'react';
import { SocialAuthProvider } from 'constants/auth';
import FaceBookIcon from 'svg/FaceBookIcon';
import GoogleIcon from 'svg/GoogleIcon';
import { Button } from 'components/Button';

interface Props {
  email: string;
  /** Sign-in methods already on the account (may be empty under email-enumeration protection). */
  existingMethods: string[];
  disabled?: boolean;
  /** Continue with a federated provider the account already uses (signs into the existing account). */
  onUseProvider: (provider: SocialAuthProvider) => void;
  /** Send the user to the email/password login flow (prefilled with their email). */
  onLogin: () => void;
}

/**
 * Shown when email/password signup hits `auth/email-already-in-use`. Instead of a dead-end "log in
 * instead" message, we surface the actual method(s) the account uses (from fetchSignInMethodsForEmail)
 * as one-tap actions. When methods are unknown (enumeration protection returns []), we fall back to
 * offering every option — picking the wrong one just routes into the normal linking/login flow.
 */
export default function ExistingAccountNotice({
  email,
  existingMethods,
  disabled,
  onUseProvider,
  onLogin,
}: Props) {
  const hasGoogle = existingMethods.includes('google.com');
  const hasFacebook = existingMethods.includes('facebook.com');
  const hasPassword = existingMethods.includes('password');
  const useFallback =
    existingMethods.length === 0 || !(hasGoogle || hasFacebook || hasPassword);

  const showGoogle = useFallback || hasGoogle;
  const showFacebook = useFallback || hasFacebook;
  const showLogin = useFallback || hasPassword;

  return (
    <div className="rounded-2xl border border-color-border-input-lightmode bg-color-bg-lightmode-secondary p-ds-lg dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-secondary">
      <p className="typography-product-body text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        <span className="font-semibold">{email}</span> already has a Bounce account. Continue with
        the method you used before.
      </p>

      <div className="mt-ds-lg flex flex-col gap-ds-md">
        {showGoogle && (
          <Button
            variant="secondary"
            size="lg"
            type="button"
            disabled={disabled}
            onClick={() => onUseProvider('google')}
          >
            <span className="flex items-center justify-center gap-ds-sm whitespace-nowrap">
              <GoogleIcon className="h-5 w-5 shrink-0" />
              Continue with Google
            </span>
          </Button>
        )}
        {showFacebook && (
          <Button
            variant="secondary"
            size="lg"
            type="button"
            disabled={disabled}
            onClick={() => onUseProvider('facebook')}
          >
            <span className="flex items-center justify-center gap-ds-sm whitespace-nowrap">
              <FaceBookIcon className="h-5 w-5 shrink-0 text-[#1877F2]" />
              Continue with Facebook
            </span>
          </Button>
        )}

        {showLogin && (
          <>
            {(showGoogle || showFacebook) && (
              <div className="my-ds-sm flex items-center gap-3">
                <span className="h-px flex-1 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
                <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                  or
                </span>
                <span className="h-px flex-1 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
              </div>
            )}
            <Button
              variant="brand"
              size="lg"
              type="button"
              disabled={disabled}
              onClick={onLogin}
            >
              Log in with email
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
